/**
 * PersonLink - clickable link to a person (FlowNotes user)
 * When userId is provided, renders as a link; otherwise plain text.
 * When basecampPersonId is provided (and no userId), fetches lookup and renders link if FlowNotes user found.
 */

import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/lib/api'
import { dispatchOpenPersonView } from '@/lib/person-view'

interface PersonLinkProps {
  name: string
  userId?: string | null
  /** Basecamp person ID - when provided, fetches FlowNotes userId (for external person resolution) */
  basecampPersonId?: string | number | null
  className?: string
  /** When provided, called instead of navigate (e.g. to open in panel) */
  onClick?: (userId: string) => void
  /** Workspace of the clicked row - People tab stays in that org group. */
  workspaceId?: string | null
}

export function PersonLink({ name, userId: userIdProp, basecampPersonId, className, onClick, workspaceId }: PersonLinkProps) {
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null)
  const [lookupDone, setLookupDone] = useState(false)

  const userId = userIdProp ?? resolvedUserId

  useEffect(() => {
    if (userIdProp || !basecampPersonId) {
      setLookupDone(true)
      return
    }
    const id = String(basecampPersonId)
    fetchWithAuth(`/api/people/by-basecamp-id/${id}`)
      .then((res) => {
        if (res.ok) return res.json()
        return null
      })
      .then((data) => {
        if (data?.userId) setResolvedUserId(data.userId)
      })
      .finally(() => setLookupDone(true))
  }, [basecampPersonId, userIdProp])

  const handleClick = (e: React.MouseEvent) => {
    if (!userId) return
    e.preventDefault()
    if (onClick) {
      onClick(userId)
    } else {
      dispatchOpenPersonView(userId, name, workspaceId || undefined)
    }
  }

  if (userId) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e as unknown as React.MouseEvent) } }}
        className={cn(
          'cursor-pointer hover:underline underline-offset-2 focus:outline-none focus:ring-1 focus:ring-primary rounded',
          className
        )}
      >
        {name}
      </span>
    )
  }

  return <span className={className}>{name}</span>
}
